import { notification } from 'antd';
import { InfoCircleOutlined, CloseCircleOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import React from 'react';

let notificationQueue = [];
const MAX_VISIBLE = 3; // only 3 notifs at the same time, prevents overlap and overflow of notifications
    
export const useNotificationHandler = () => {
    const [api, contextHolder] = notification.useNotification();

    const activeNotifications = React.useRef(new Set());
    const errorDisplayed = React.useRef(false);
    const lastErrorTime = React.useRef(0);
    const ERROR_TIMEOUT = 5000; 

    const updateNotificationPositions = () => {
        const activeNotificationsList = Array.from(activeNotifications.current);
        activeNotificationsList.forEach((key, index) => {
            const notificationElement = document.querySelector(`[data-notification-key="${key}"]`);
            if (notificationElement) {
                const yPosition = index * (notificationElement.offsetHeight + "10px");
                notificationElement.style.top = `${yPosition}px`;
                notificationElement.style.zIndex = `${1001 + index}`;
            }
        });
    };

    const createNotification = (type, message, description, duration = 5, options = {}) => {
        const key = options.key || `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        activeNotifications.current.add(key);

        let icon;
        switch (type) {
            case 'success':
                icon = <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                break;
            case 'error':
                icon = <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                break;
            case 'info':
                icon = <InfoCircleOutlined style={{ color: '#0f52b9' }} />;
                break;
            case 'loading':
                icon = <LoadingOutlined style={{ color: '#1890ff' }} />;
                break;
            default:
                icon = <InfoCircleOutlined style={{ color: '#0f52b9' }} />;
        }

        if (options.icon) {
            icon = options.icon;
        }

        api[type === 'loading' ? 'info' : type]({
            key,
            message,
            description,
            icon,
            style: {
                width: 300,
                padding: '14px',
                backgroundColor: 'white',
                whiteSpace: 'pre-line',
                borderRadius: '6px',
                position: 'fixed',
                top: 0,
                right: 0,
                zIndex: 1001,
            },
            className: 'custom-notification',
            duration,
            onClose: () => {
                activeNotifications.current.delete(key);
                updateNotificationPositions();
            },
            ...options
        });

        setTimeout(() => {
            const notificationElement = document.querySelector(`[data-notification-key="${key}"]`);
            if (!notificationElement) {
                const notificationElements = document.querySelectorAll('.ant-notification-notice');
                notificationElements.forEach(el => {
                    if (!el.getAttribute('data-notification-key')) {
                        el.setAttribute('data-notification-key', key);
                    }
                });
            }
            updateNotificationPositions();
        }, 10);

        return key;
    };

    const notifySuccess = (message, description, duration, options) =>
        createNotification('success', message, description, duration, options);

    const notifyError = (message, description, duration, options) =>
        createNotification('error', message, description, duration, options);

    const notifyInfo = (message, description, duration, options) =>
        createNotification('info', message, description, duration, options);

    const notifyLoading = (message, description, duration, options) =>
        createNotification('loading', message, description, duration,
            { ...options, icon: <LoadingOutlined style={{ color: '#1890ff' }} /> }
        );

    const closeNotification = (key) => {
        if (!key) return;
        api.destroy(key);
        activeNotifications.current.delete(key);
        updateNotificationPositions();
    };


    // For mapcomponent.js 
    const openNotification = (layers) => {
        if (activeNotifications.current.size > 0) {
            const currentKeys = Array.from(activeNotifications.current);
            currentKeys.forEach(key => api.destroy(key));
            activeNotifications.current.clear();
        }

        if (!layers || !Array.isArray(layers) || layers.length === 0) {
            return;
        }

        const description = layers.map(layer => `Loading ${layer}`).join('\n');
        errorDisplayed.current = false;

        return createNotification(
            'info',
            'Getting Your Layers Ready',
            description,
            5,
            {
                icon: <InfoCircleOutlined style={{ color: '#0f52b9' }} />
            }
        );
    };

    const handleError = (message, layer = 'Unknown Layer') => {
        const now = Date.now();
        if (now - lastErrorTime.current < ERROR_TIMEOUT) return;
        lastErrorTime.current = now;

        errorDisplayed.current = true;

        return createNotification(
            'error',
            'Error loading a layer',
            message,
            5,
            {
                icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            }
        );
    };

    return {
        contextHolder,
        notifySuccess,
        notifyError,
        notifyInfo,
        notifyLoading,
        closeNotification,
        openNotification,
        handleError
    };
};

const applyNotificationStyles = () => {
    if (typeof document === 'undefined') return;

    if (document.getElementById('notification-handler-styles')) return;

    const style = document.createElement('style');
    style.id = 'notification-handler-styles';
    style.textContent = `
    .ant-notification-notice {
      position: fixed !important;
      margin-bottom: 0 !important;
      margin-right: 0 !important;
      transition: top 0.3s ease, right 0.3s ease;
    }
  `;
    document.head.appendChild(style);
};

applyNotificationStyles();